import React, { useState } from 'react';
import { Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

export default function WeeklyAvailabilityGrid({
  availability,
  editable,
  profileAvailabilityLoading,
  saveAvailability,
}: {
  availability?: boolean[];
  editable: boolean;
  profileAvailabilityLoading?: boolean;
  // eslint-disable-next-line no-unused-vars -- callback type signature
  saveAvailability?: (values: boolean[]) => unknown;
}) {
  const { t } = useTranslation();
  const normalized = Array.from({ length: 21 }, (_, index) => !!availability?.[index]);
  const [draft, setDraft] = useState(normalized);
  const dirty = editable && draft.some((value, index) => value !== normalized[index]);
  // Read-only views follow the latest server/parent data; editable drafts stay local.
  const values = editable ? draft : availability;
  if (!editable && (!values || values.length !== 21)) {
    return <p className="profile-availability-empty">{t('profile.availability.notShared')}</p>;
  }

  return (
    <>
      <div className={`profile-availability ${editable ? 'editable' : ''}`}>
        <table className="availability-table">
          <caption>{t('profile.availability.title')}</caption>
          <thead>
            <tr>
              <th scope="col">
                <span className="visually-hidden">{t('mobile.date')}</span>
              </th>
              {['morning', 'noon', 'evening'].map(period => (
                <th scope="col" key={period}>
                  {t(`profile.availability.${period}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'].map((day, dayIndex) => (
              <tr key={day}>
                <th scope="row">{t(`mobile.days.${day}`)}</th>
                {['morning', 'noon', 'evening'].map((period, periodIndex) => {
                  const index = dayIndex + periodIndex * 7;
                  const available = !!values?.[index];
                  const mark = available ? (
                    <CheckOutlined aria-hidden="true" />
                  ) : (
                    <span aria-hidden="true">—</span>
                  );
                  return (
                    <td key={period}>
                      {editable ? (
                        <button
                          type="button"
                          disabled={profileAvailabilityLoading}
                          aria-label={`${t(`mobile.days.${day}`)}, ${t(
                            `profile.availability.${period}`,
                          )}`}
                          aria-pressed={available}
                          onClick={() =>
                            setDraft(previous =>
                              previous.map((value, slot) => (slot === index ? !value : value)),
                            )
                          }
                        >
                          {mark}
                        </button>
                      ) : (
                        <span className="availability-table-value" data-available={available}>
                          {mark}
                          <span className="visually-hidden">
                            {t(`profile.availability.${available ? 'available' : 'unavailable'}`)}
                          </span>
                        </span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dirty && (
        <div className="settings-save-bar" role="region" aria-label={t('profile.availability.unsaved', 'Kaydedilmemiş müsaitlik değişiklikleri')}>
          <span>{t('profile.availability.unsaved', 'Kaydedilmemiş müsaitlik değişiklikleri')}</span>
          <div>
            <Button onClick={() => setDraft(normalized)} disabled={profileAvailabilityLoading}>
              {t('profile.availability.discard', 'Vazgeç')}
            </Button>
            <Button type="primary" loading={profileAvailabilityLoading} onClick={() => saveAvailability?.(draft)}>
              {t('profile.availability.save')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

WeeklyAvailabilityGrid.defaultProps = {
  availability: undefined,
  profileAvailabilityLoading: false,
  saveAvailability: undefined,
};
